import React, { useState } from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";

const PatientQuestions = () => {
  const [openCategory, setOpenCategory] = useState("acton");

  const categories = {
    acton: {
      title: "📍 Finding Local Care in Acton",
      questions: [
        {
          q: "I've just moved to Acton and need a reliable, friendly dentist who won't judge me if I haven't been in a while. Are you taking new patients?",
          a: "We completely understand that finding a new practice can be daunting, and we promise there is absolutely zero judgement here! We are currently welcoming new patients from the local area. We'd love to show you around and get your dental health back on track.",
          link: "/dentist-acton",
          linkText: "Learn more about joining our practice"
        },
        {
          q: "I live in Acton and really want to straighten my teeth, but as an adult, I absolutely refuse to wear metal braces. What are my options nearby?",
          a: "You are definitely not alone in that! Most of our adult patients want a discreet solution. We offer clear, virtually invisible aligners right here in the practice, so you don't have to travel far to get a straighter smile without the metal wires.",
          link: "/invisalign-acton",
          linkText: "Check out how Invisalign works"
        },
        {
          q: "I chipped my front tooth and I have a huge event coming up. I'm panicking—can someone in Acton fix this quickly so it looks natural?",
          a: "Try not to panic! We can absolutely sort this out for you. We use advanced, tooth-coloured resins that we sculpt directly onto your tooth, often in just one single visit. It's quick, painless, and blends perfectly with your natural teeth.",
          link: "/composite-bonding-acton",
          linkText: "Read about composite bonding"
        },
        {
          q: "I'm in absolute agony with a toothache right now and live near Acton. How quickly can someone see me?",
          a: "Toothaches are miserable, and we don't want you sitting in pain. Please contact us immediately. We keep dedicated time slots open every day specifically to get people out of pain as fast as possible.",
          link: "/emergency-dentist-acton",
          linkText: "Find our urgent contact details"
        },
        {
          q: "I've always been really self-conscious about my smile, but I don't even know where to start or what treatments I'd need. Do you offer consultations in Acton?",
          a: "We hear this all the time, and the hardest step is just walking through the door! We offer dedicated consultations where we just sit down, listen to what bothers you, and look at all your options—no pressure, no jargon.",
          link: "/cosmetic-dentist-acton",
          linkText: "Learn more about smile design"
        },
        {
          q: "I lost a tooth a while ago and I hate the gap. I'm looking for a permanent fix in the Acton area—is an implant the right choice for me?",
          a: "A gap can really knock your confidence, and it can make eating tricky, too. For most people, an implant is the absolute best solution because it looks, feels, and functions exactly like a natural tooth. We can assess your jaw and talk you through the process right here.",
          link: "/implants-acton",
          linkText: "Find out more about implants"
        }
      ]
    },
    replacement: {
      title: "🦷 Missing Teeth & Tooth Replacement",
      questions: [
        {
          q: "I absolutely hate wearing my removable denture. It moves around when I eat and feels bulky. Is there a way to replace my missing tooth with something permanent?",
          a: "We totally get it—removable teeth can be a real hassle. The gold standard is replacing the root and the tooth permanently, so it stays put 24/7. It feels just like having your own tooth back.",
          link: "/services/dental-implants",
          linkText: "Discover dental implants"
        },
        {
          q: "My teeth are failing, and I'm facing losing them all. The thought of my teeth sitting in a glass of water at night is making me so depressed. What else can I do?",
          a: "This is a very common fear, but dentistry has evolved incredibly! You do not have to settle for traditional, removable dentures. We can secure a full, beautiful set of fixed teeth using just a few precise anchors, often in a single day.",
          link: "/services/all-on-4-implants",
          linkText: "Learn about All-on-4"
        },
        {
          q: "I'm missing a tooth and I really don't want surgery for an implant, but I also refuse to wear a denture I have to take out at night. What's the middle ground?",
          a: "If the teeth on either side of the gap are healthy, we can use them to anchor a custom-made, permanent replacement tooth. It's fixed firmly in place, looks totally natural, and doesn't require any surgical procedures.",
          link: "/services/bridges",
          linkText: "Read about bridges"
        },
        {
          q: "My current dentures are so loose they rub my gums raw, and going out to a restaurant is a nightmare. Can I get a set that actually fits properly and lets me eat normal food?",
          a: "You shouldn't have to suffer through meals! A well-made set of bespoke dentures should fit snugly and function beautifully. We take incredibly precise impressions to craft teeth that look natural and sit comfortably, so you can eat with confidence.",
          link: "/services/dentures",
          linkText: "See our denture solutions"
        }
      ]
    },
    cosmetic: {
      title: "✨ Smile Makeovers & Cosmetic",
      questions: [
        {
          q: "My teeth are permanently stained, a bit misshapen, and no amount of whitening helps. I just want that flawless, bright smile I see on TV. How do I get that?",
          a: "When teeth have stubborn structural stains or shape issues, standard whitening won't cut it. We can craft ultra-thin, custom-made porcelain covers that fit perfectly over your natural teeth, giving you a stunning, permanent transformation.",
          link: "/services/porcelain-veneers",
          linkText: "Read about porcelain veneers"
        },
        {
          q: "I want to improve the shape of my teeth and close some small gaps, but the idea of shaving my natural teeth down terrifies me. Is there a gentler way?",
          a: "Absolutely. We can use a highly polished, tooth-coloured resin layered directly onto your existing teeth. It requires little to no drilling of your natural tooth structure, making it a fantastic, minimally invasive option.",
          link: "/services/composite-veneers",
          linkText: "See composite veneers"
        },
        {
          q: "I've wasted so much money on strips and special toothpastes from the chemist, and my teeth are still yellow. How can I get results that actually last?",
          a: "Over-the-counter products simply aren't legally allowed to be strong enough to make a real, lasting difference. We use professional-grade gels combined with custom-fit trays that safely lift deep stains from the inside out.",
          link: "/services/teeth-whitening",
          linkText: "Get professional whitening details"
        },
        {
          q: "I really want to change my smile, but I'm absolutely terrified that I'll spend all this money and hate how it looks at the end. Can I see the result before we do anything?",
          a: "We wouldn't want to fly blind either! We use advanced 3D scanning and software to design your new smile digitally. We can even create a physical 'mock-up' that you can try on in your own mouth to test-drive your new look before we touch a single tooth.",
          link: "/services/digital-smile-design",
          linkText: "See the technology in action"
        }
      ]
    },
    straightening: {
      title: "😁 Straightening & Alignment",
      questions: [
        {
          q: "I wore traditional braces as a teenager, but I lost my retainers and my teeth have completely shifted back. Can I straighten them again without metal tracks?",
          a: "You'd be amazed how many adults are in the exact same boat! The good news is that we can gently guide your teeth back into position using a series of clear, comfortable, and removable trays. Nobody will even know you're wearing them.",
          link: "/services/invisalign",
          linkText: "Find out more about Invisalign"
        }
      ]
    },
    complex: {
      title: "🛠️ Complex & Restorative Care",
      questions: [
        {
          q: "My mouth is a mess. I have worn-down teeth, missing teeth, and it hurts to chew. I feel so embarrassed and overwhelmed. Can this actually be fixed?",
          a: "Please don't be embarrassed—we see complex cases every single week, and there is always a way forward. We can create a comprehensive, step-by-step plan to rebuild your bite, restore your teeth, and get you chewing comfortably again.",
          link: "/services/full-mouth-reconstruction",
          linkText: "See how we approach complex care"
        },
        {
          q: "My tooth cracked right down the middle today, but it doesn't hurt yet. I really don't want to lose it—is there a way to save it and make it strong again?",
          a: "As long as the root is intact, we can usually save the tooth! We can design and fit a custom 'cap' that fits snugly over the entire tooth, holding it together, restoring its strength, and making it look brand new.",
          link: "/services/crowns",
          linkText: "Learn how we protect damaged teeth"
        }
      ]
    },
    emergency: {
      title: "🚨 Urgent & Emergency Care",
      questions: [
        {
          q: "I was eating something hard and half my filling just broke off. It's really sensitive to cold air. Do I need to come in today?",
          a: "Yes, it's always best not to wait with a broken filling. An exposed tooth can become infected or break further if left unprotected. Call us right away, and we will get you sorted before it turns into a bigger, more painful problem.",
          link: "/services/emergency-dentist",
          linkText: "Here's what to do"
        }
      ]
    },
    anxiety: {
      title: "😰 Dental Anxiety & Phobia",
      questions: [
        {
          q: "I know my teeth need work, but I haven't been to the dentist in years because I am absolutely petrified. Is there any way you can just knock me out for it?",
          a: "Dental anxiety is incredibly common, and we will never judge you for it. While we don't use general anaesthetic, we offer a brilliant alternative that makes you feel deeply relaxed, sleepy, and completely separated from the procedure. You likely won't remember a thing!",
          link: "/services/sedation",
          linkText: "Find out how we make visits stress-free"
        }
      ]
    },
    jaw: {
      title: "😬 Jaw Pain, Grinding & TMJ",
      questions: [
        {
          q: "I wake up every morning with a dull headache and a really sore jaw. My jaw also clicks loudly when I eat. What is going on?",
          a: "This sounds exactly like an issue with your jaw joint, often caused by stress, clenching, or an uneven bite. It's incredibly frustrating to live with, but we have specific therapies and assessments to help relieve the tension and get your jaw functioning smoothly again.",
          link: "/services/tmj-dentistry",
          linkText: "Learn about TMJ relief"
        },
        {
          q: "My partner tells me I grind my teeth terribly in my sleep, and I've noticed my front teeth are starting to look flat and worn. How do I stop this from getting worse?",
          a: "Sleep grinding is very destructive, but it's easily managed. We can custom-fabricate a slim, comfortable guard that you wear just while you sleep. It acts as a protective barrier, taking all the force so your teeth don't have to.",
          link: "/services/splints",
          linkText: "Find out how to protect your smile"
        },
        {
          q: "I clench my jaw so intensely that the muscles in my face and temples ache all day long. Painkillers barely touch it. Is there anything that can just force these muscles to relax?",
          a: "Yes, there is a very effective solution for this! By using precise, targeted muscle-relaxing injections (the same protein used for facial aesthetics), we can calm the overactive jaw muscles, significantly reducing the pain and tension without affecting your smile or ability to chew.",
          link: "/services/botox",
          linkText: "Learn more about Botox relief"
        },
        {
          q: "I've realised I'm constantly clenching my teeth tight during the day when I'm working or stressed. Is this going to ruin my teeth over time?",
          a: "It definitely can. Constant clenching puts massive stress on the enamel, leading to micro-cracks, sensitivity, and eventual breakages. We can help you manage this habit, assess the damage, and protect your teeth before the wear becomes severe.",
          link: "/services/bruxism",
          linkText: "Check out our management strategies"
        }
      ]
    }
  };

  return (
    <>
      <SEO
        title="Your Questions Answered | Restore.Dental Patient Q&A"
        description="Real questions from real patients about dental treatments in Acton. Get honest answers about implants, Invisalign, emergency care, and cosmetic dentistry."
        path="/patient-questions"
        keywords={["dental questions", "patient questions", "dentist Acton", "dental FAQ", "Invisalign questions", "dental implants FAQ"]}
      />
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Questions We Hear Every Day
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real concerns from real patients. Honest, empathetic answers with zero judgment.
              <br />
              <span className="text-teal-600 font-medium">You're not alone in wondering about these things.</span>
            </p>
          </div>

          {/* Category Buttons */}
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {Object.entries(categories).map(([key, cat]) => (
              <button
                key={key}
                onClick={() => setOpenCategory(key)}
                className={`px-4 py-2 rounded-lg font-medium transition ${
                  openCategory === key
                    ? "bg-teal-600 text-white shadow-lg"
                    : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-200"
                }`}
              >
                {cat.title}
              </button>
            ))}
          </div>

          {/* Questions */}
          <div className="space-y-6">
            {categories[openCategory].questions.map((item, idx) => (
              <div
                key={idx}
                className="bg-white rounded-xl shadow-md border border-gray-200 p-6 hover:shadow-lg transition"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  "{item.q}"
                </h3>
                <p className="text-gray-700 mb-4 leading-relaxed">{item.a}</p>
                <Link
                  to={item.link}
                  className="inline-flex items-center text-teal-600 hover:text-teal-700 font-medium"
                >
                  {item.linkText}
                  <svg
                    className="w-4 h-4 ml-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </Link>
              </div>
            ))}
          </div>

          {/* CTA Footer */}
          <div className="mt-12 text-center bg-teal-50 rounded-xl p-8 border-2 border-teal-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              Still Have Questions?
            </h2>
            <p className="text-gray-700 mb-6">
              We're here to listen, not to lecture. Book a no-pressure consultation and let's chat.
            </p>
            <a
              href="tel:07787814064"
              className="inline-block bg-teal-600 hover:bg-teal-700 text-white font-semibold px-8 py-4 rounded-lg shadow-lg transition"
            >
              📞 Call 07787 814 064
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default PatientQuestions;
